import { Button } from '@customerio/pluma-components/react';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { useAuth } from '@/services/auth';
import type { Integration } from '@/services/db/integration/entity';
import { CLICKED_ON, landingPageButtonClickedProperties } from '../analytics';
import { resolveCta } from '../cta';
import { useAnalytics } from '@/services/analytics';

type CtaButtonProps = {
  integration: Integration;
  workspaceId: string;
};

/**
 * The integration's primary action.
 *
 * A destination inside another embedded app goes through `useSafeNavigate` so
 * that app hears the navigation; leaving it to the router would strand a booted
 * instance on its previous route. Modified clicks are left to the browser, so
 * open-in-new-tab, new window and download all still behave.
 */
export function CtaButton({ integration, workspaceId }: CtaButtonProps) {
  const { track } = useAnalytics();
  const safeNavigate = useSafeNavigate();
  const auth = useAuth();
  const cta = resolveCta(integration, workspaceId, {
    accountEmail: auth.account.email,
    accountId: auth.account.id,
    userId: auth.user.id,
  });

  if (cta.kind === 'unavailable') {
    return null;
  }

  const trackClick = () => {
    void track(
      'integration',
      'integrationLandingPageButtonClicked',
      landingPageButtonClickedProperties(integration, CLICKED_ON.button),
    );
  };

  const isEmbeddedApp = cta.kind === 'pipelines' || cta.kind === 'journeys';

  return (
    <Button
      href={cta.href}
      variant="primary"
      icon="exit"
      iconPosition="trailing"
      pr="100"
      isExternal={cta.kind === 'external'}
      onClick={(event) => {
        trackClick();

        // Anything the browser has its own answer for — new tab, new window,
        // download, middle-click — is left alone.
        if (!isEmbeddedApp || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
          return;
        }

        event.preventDefault();
        safeNavigate(cta.href);
      }}
    >
      {cta.label}
    </Button>
  );
}
